import { create } from "zustand";

/**
 * Table Types - represents all tables in the application
 */
export type TableType =
  | "nodes"
  | "beams"
  | "supports"
  | "loads"
  | "loadcases"
  | "section-properties"
  | null;

/**
 * Cursor Mode Types - represents all possible cursor functionalities
 */
export type CursorMode =
  | "node-creation"
  | "beam-creation"
  | "prep-mode"
  | "divide-beam"
  | "support-apply-click"
  | "load-apply-click"
  | "property-apply-click"
  | "loadcase-apply-click"
  | "select-nodes"
  | "select-beams"
  | "select-all"
  | null;

/**
 * Cursor Mode Messages - maps each cursor mode to its display message
 */
export const CURSOR_MODE_MESSAGES: Record<NonNullable<CursorMode>, string> = {
  "node-creation": "Node Creation: Click on the canvas to create a node",
  "beam-creation": "Beam Creation: Click on two nodes to create a beam",
  "prep-mode":
    "Prep Mode Active - Select Beam first then click Node to draw Perpendicular line",
  "divide-beam":
    "Divide Beam: Click on a beam to split it at the midpoint. Press ESC to cancel.",
  "support-apply-click":
    "Click on a node to apply the selected support. Press ESC to cancel.",
  "load-apply-click":
    "Click on a node to apply the selected load. Press ESC to cancel.",
  "property-apply-click":
    "Click on a beam to apply the selected property. Press ESC to cancel.",
  "loadcase-apply-click":
    "Click on a beam to apply the selected loadcase. Press ESC to cancel.",
  "select-nodes": "Node Selection Mode - Click or drag to select nodes only",
  "select-beams": "Beam Selection Mode - Click or drag to select beams only",
  "select-all": "All Selection Mode - Click or drag to select nodes and beams",
};

interface GlobalSelectionState {
  // Active table selection
  activeTable: TableType;
  activeRowId: string | null;
  activeRowData: any | null;

  // Active cursor mode
  activeCursorMode: CursorMode;
  cursorModeData: any | null;

  // Actions for table selection
  setActiveTableRow: (
    table: TableType,
    rowId: string | null,
    rowData?: any
  ) => void;
  clearTableSelection: () => void;
  isRowActive: (table: TableType, rowId: string) => boolean;

  // Actions for cursor mode
  setActiveCursorMode: (mode: CursorMode, data?: any) => void;
  clearCursorMode: () => void;
  isCursorModeActive: (mode: CursorMode) => boolean;
  getCursorModeMessage: () => string | null;

  // Utility to clear everything
  clearAll: () => void;
}

export const useGlobalSelectionStore = create<GlobalSelectionState>(
  (set, get) => ({
    // ============ INITIAL STATE ============
    activeTable: null,
    activeRowId: null,
    activeRowData: null,
    activeCursorMode: null,
    cursorModeData: null,

    // ============ TABLE SELECTION ACTIONS ============

    /**
     * Set active table row - automatically clears other table selections
     */
    setActiveTableRow: (table, rowId, rowData = null) => {
      const currentState = get();

      // If selecting the same row, do nothing
      if (
        currentState.activeTable === table &&
        currentState.activeRowId === rowId
      ) {
        return;
      }

      // Set new selection (automatically clears previous)
      set({
        activeTable: table,
        activeRowId: rowId,
        activeRowData: rowData,
      });
    },

    /**
     * Clear table selection
     */
    clearTableSelection: () => {
      set({
        activeTable: null,
        activeRowId: null,
        activeRowData: null,
      });
    },

    /**
     * Check if a specific row is active
     */
    isRowActive: (table, rowId) => {
      const state = get();
      return state.activeTable === table && state.activeRowId === rowId;
    },

    // ============ CURSOR MODE ACTIONS ============

    /**
     * Set active cursor mode - automatically clears previous mode
     */
    setActiveCursorMode: (mode, data = null) => {
      const currentState = get();

      // If setting the same mode, do nothing
      if (currentState.activeCursorMode === mode) {
        return;
      }

      // Clear previous cursor mode and set new one
      set({
        activeCursorMode: mode,
        cursorModeData: data,
      });
    },

    /**
     * Clear cursor mode
     */
    clearCursorMode: () => {
      set({
        activeCursorMode: null,
        cursorModeData: null,
      });
    },

    /**
     * Check if a specific cursor mode is active
     */
    isCursorModeActive: (mode) => {
      const state = get();
      return state.activeCursorMode === mode;
    },

    /**
     * Get the message for the current cursor mode
     * Returns null if no cursor mode is active
     */
    getCursorModeMessage: () => {
      const state = get();
      if (!state.activeCursorMode) return null;
      return CURSOR_MODE_MESSAGES[state.activeCursorMode] || null;
    },

    // ============ UTILITY ACTIONS ============

    /**
     * Clear all selections and modes
     */
    clearAll: () => {
      set({
        activeTable: null,
        activeRowId: null,
        activeRowData: null,
        activeCursorMode: null,
        cursorModeData: null,
      });
    },
  })
);
